/* eslint-disable react/jsx-props-no-spreading */

import { useRequest } from 'ahooks';
import { Button, Drawer, message, Modal, Space, Row, Col, Form } from 'antd';
import LocaleContext from 'locales';
import { getErrorFields, getValidationMsgs } from 'pages/utils';
import React, { useContext, useEffect, useState } from 'react';
import { updateApprovalLine, getRequestDetail, requestApproval } from 'services/websocket/approval';
import ConfigContext from 'store/ConfigContext';
import DesktopCreate from './DesktopCreate';
import DesktopDelete from './DesktopDelete';
import DesktopReinstall from './DesktopReinstall';
import DesktopUpdate from './DesktopUpdate';
import DriveCreate from './DriveCreate';
import DriveDelete from './DriveDelete';
import DriveUpdatePermission from './DriveUpdatePermission';
import DriveUpdateSize from './DriveUpdateSize';
import DriveUsetime from './DriveUsetime';
import FileDownload from './FileDownload';
import FileDownloadEmergency from './FileDownloadEmergency';
import FileDownloadLarge from './FileDownloadLarge';
import FileDownloadTranz from './FileDownloadTranz';
import FileUpload from './FileUpload';
import FileUploadLarge from './FileUploadLarge';
import Comment from './components/Comment';

// 서비스 신청 상세 화면
function DetailType(props) {
  const { type } = props;
  switch (type) {
    case 'approval.types.create-vd':
      return <DesktopCreate {...props} />;
    case 'approval.types.change-spec-vd':
      return <DesktopUpdate {...props} />;
    case 'approval.types.reset-vd':
      return <DesktopReinstall {...props} />;
    case 'approval.types.delete-vd':
      return <DesktopDelete {...props} />;
    case 'approval.types.create-share-drive':
      return <DriveCreate {...props} />;
    case 'approval.types.modify-drive-size':
      return <DriveUpdateSize {...props} />;
    case 'approval.types.modify-drive-permission':
      return <DriveUpdatePermission {...props} />;
    case 'approval.types.delete-drive':
      return <DriveDelete {...props} />;
    case '드라이브 사용기간 변경 신청':
      return <DriveUsetime {...props} />;
    case '파일다운로드 신청':
      return <FileDownload {...props} />;
    case '파일다운로드 신청(긴급)':
      return <FileDownloadEmergency {...props} />;
    case '파일다운로드 신청(대용량)':
      return <FileDownloadLarge {...props} />;
    case '파일다운로드 신청(Tranzfile)':
      return <FileDownloadTranz {...props} />;
    case '파일업로드 신청':
      return <FileUpload {...props} />;
    case '파일업로드 신청(대용량)':
      return <FileUploadLarge {...props} />;

    default:
      return null;
  }
}

/**
 * 서비스 신청 상세화면
 * @param {string} id 서비스 신청 ID
 * @param {string} type 서비스 신청 타입 (대기중 탭에서만 전달됨, 완료 탭에서는 undefined)
 * @param {function} onDetailClose 상세화면 닫힐 때 상위 컴포넌트의 콜백 함수
 * @returns ReactNode
 */
function Detail(props) {
  const {
    type: initialType,
    onDetailClose,
    id,
    location,
    onRequestCancel,
    isCompleted = false,
  } = props;
  const config = useContext(ConfigContext);

  const [visible, setVisible] = useState(false);

  const [detailData, setDetailData] = useState(undefined);
  const [isSaveComment, setIsSaveComment] = useState(false);

  // refs
  const [form] = Form.useForm();
  const [commnetForm] = Form.useForm();

  // hooks
  const { locale } = useContext(LocaleContext);

  // variables
  const loginedUserId = localStorage.getItem('userId'); // 현재 로그인한 사용자 ID
  const isApproval = location.pathname === '/approval'; // 승인 업무 상황인가?
  const [isWaitState, setIsWaitState] = React.useState(false); // 상태가 대기 중인가?
  const isCreate = !id; // 생성인가?

  // ID 또는 type이 변경되면 Drawer visible 업데이트
  useEffect(() => {
    setVisible(!!id || !!initialType);
  }, [id, initialType]);

  // Drawer가 닫힐 때 Form 리셋
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      // commnetForm이 존재하고 연결된 Form이 있을 때만 resetFields 호출
      if (commnetForm && commnetForm.resetFields) {
        try {
          commnetForm.resetFields();
        } catch (error) {
          // Form이 연결되지 않은 경우 무시
          console.warn('Comment form is not connected:', error);
        }
      }
    }
  }, [visible, form, commnetForm]);

  // 상세 정보 조회 API
  const { run } = useRequest(getRequestDetail, {
    manual: true,
    onSuccess: (response) => {
      if (response?.data) {
        // 대기 상태인지 확인
        setDetailData(response.data);
        setIsWaitState(response.data.state === 'approval.state.waiting');
      }
    },
    onError: (error) => {
      message.error(`${locale.resource['txt.lookup.failed']}: ${locale.resource[error.message]}`);
    },
  });

  // id가 있으면 상세 정보 조회
  useEffect(() => {
    if (id) {
      run(id);
    }
  }, [id]);

  // 상세 페이지 닫기
  const handleClose = () => {
    setVisible(false);
    setDetailData(undefined);
    onDetailClose();
  };

  // 승인 또는 반려하기
  const doApprove = async (appr) => {
    if (config.siteName === 'lgensol-usa') {
      const validComment = await commnetForm.validateFields(['comments']);

      if (!isSaveComment) {
        commnetForm.setFields([
          { name: ['comments'], errors: [locale.resource['error.approval.require-comments']] },
        ]);
        return;
      }

      if (!validComment.comments || validComment.comments.replace(/\s+/g, '') === '') {
        commnetForm.setFields([
          {
            name: ['comments'],
            errors: [locale.resource['error.approval.require-comments']],
          },
        ]);
        return;
      }
    }

    const payload = { requestId: id, approverId: loginedUserId, status: appr };
    const res = await updateApprovalLine(payload);
    if (res.status === 'error') {
      const { invalidParams } = res;
      const errors = getErrorFields(getValidationMsgs(invalidParams));
      form.setFields(errors);
      const generalErrors = errors.find((el) => el.name[0] === 'generalValidationError'); // generalValidationError는 무조건 하나만 받고 errors를 배열로 여러 개 받는다.
      if (generalErrors) {
        Modal.error({
          title: `${locale.resource['approval.state.approved']}/${locale.resource['approval.state.rejected']} ${locale.resource['txt.failed']}`,
          content: generalErrors.errors.map((el) => <div>{el}</div>),
        });
      }
      return;
    }
    message.success(
      `${locale.resource['approval.state.approved']}/${locale.resource['approval.state.rejected']} ${locale.resource['txt.successed']}`,
    );
    handleClose();
  };

  // 컴포넌트: Drawer footer
  const DrawerFooter = () => (
    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
      {!isCompleted && isApproval ? (
        <>
          <div>
            {isWaitState && (
              <Button danger onClick={() => doApprove('approval.state.rejected')}>
                {locale.resource['approval.state.rejected']}
              </Button>
            )}
          </div>
          <Space>
            <Button onClick={handleClose}>{locale.resource['txt.close']}</Button>
            {!isCreate && isWaitState && (
              <Button type="primary" onClick={() => doApprove('approval.state.approved')}>
                {locale.resource['approval.state.approved']}
              </Button>
            )}
          </Space>
        </>
      ) : (
        <>
          <div>
            {!isCreate && isWaitState && !isCompleted && (
              <Button danger onClick={() => onRequestCancel([id], locale, () => handleClose())}>
                {locale.resource['txt.application-cancel']}
              </Button>
            )}
          </div>
          <Space>
            <Button onClick={handleClose}>{locale.resource['txt.close']}</Button>
            {
              // 수정 기능은 제공하지 않는다.
              isCreate && (
                <Button
                  type="primary"
                  onClick={async () => {
                    await form.validateFields();
                    form.submit();
                  }}
                >
                  {locale.resource['txt.request']}
                </Button>
              )
            }
          </Space>
        </>
      )}
    </Space>
  );

  // 저장 시
  const onFinishDetail = async (payload) => {
    try {
      const result = await requestApproval(payload);
      if (result.status === 'error') {
        const { invalidParams } = result;
        const reason = getValidationMsgs(invalidParams);
        const errors = getErrorFields(reason);
        form.setFields(errors);
        const generalErrors = errors.find(
          (el) => el.name[0] === 'generalValidationError' || el.name[0].replace(/\s+/g, '') === '',
        ); // generalValidationError는 무조건 하나만 받고 errors를 배열로 여러 개 받는다.
        if (generalErrors) {
          Modal.error({
            title: locale.resource['txt.failed-to-save'],
            content: generalErrors.errors.map((el) => <div>{el}</div>),
          });
        }
        return;
      }
      message.success(locale.resource['txt.succeeded-to-save']);
      onDetailClose();
    } catch (e) {
      message.error(e.message);
    }
  };

  // 표시할 type 결정
  // - 대기중 탭에서는 초기에 전달된 type 사용
  // - 완료 탭 목록에는 type이 들어오지 않으므로, API 응답의 type 사용

  // const detailData = data?.data;
  const type = initialType || detailData?.type;

  return (
    <Drawer
      title={
        <Space>
          <div>{locale.resource['txt.request-approval']}:</div>
          {type && locale.resource[type]}
        </Space>
      }
      visible={visible}
      width={840}
      onClose={handleClose}
      destroyOnClose
      footer={<DrawerFooter />}
    >
      <DetailType
        {...props}
        type={type}
        form={form}
        detailData={isCreate ? null : detailData}
        onFinishDetail={onFinishDetail}
        setIsWaitState={setIsWaitState}
        isCreate={isCreate}
      />
      {(!isCreate || isApproval) && (
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Comment
              id={id}
              isApproval={isApproval}
              isCompleted={!isWaitState}
              form={commnetForm}
              setIsSaveComment={setIsSaveComment}
            />
          </Col>
        </Row>
      )}
    </Drawer>
  );
}

export default Detail;
